import React, { useEffect, useState } from "react";
import {
  plansDataMonthly,
  plansDataAnnual,
  httpConstants
} from "../../constants";
import Utils from "../../utility";
import UpgradeSubscription from "./upgradeSubscription";
import {
  upgradeSubscriptionPlan,
  verifyUserByAuth,
  createSubscriptionCheckout,
  getPlans
} from "../../services/userService";
import { useDispatch } from "react-redux";
import {
  hideLoader,
  showLoader,
  updateUserDetail
} from "../../slices/userSlice";
import { sessionManager } from "../../managers/sessionManager";
import DiscountCodeInput from "../../common/components/DiscountCodeInput";

const PricingPopup = ({ handlePricingPopup }) => {
  const dispatch = useDispatch();
  const userInfo = JSON.parse(localStorage.getItem("info"));
  const isPlanPurchased = userInfo?.subscriptionDetails;

  const [planBasic, setPlanBasic] = useState(false);
  const [planData, setPlanData] = useState(plansDataMonthly || []);
  // Live plan content from the admin dashboard; bundled constants are the fallback.
  const [monthlyPlans, setMonthlyPlans] = useState(plansDataMonthly || []);
  const [annualPlans, setAnnualPlans] = useState(plansDataAnnual || []);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [upgradeSubscriptionOpen, setUpgradeSubscriptionOpen] = useState(false);
  const [priceId, setPriceId] = useState("");
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState("");

  const handleUpgradeSubscriptionPopup = () => {
    setUpgradeSubscriptionOpen(!upgradeSubscriptionOpen);
  };
  // FUNCTION TO UPDATE THE SUBSCRIPTION PLAN
  const updateSubscriptionDetails = async () => {
    const token = String(
      sessionManager.getDataFromCookies(httpConstants.HEADERS.AUTH0_ID_TOKEN)
    );

    if (!priceId) {
      Utils.failureToastMessage("You have not selected any plan yet");
      return;
    }

    try {
      dispatch(showLoader());
      const requestBody = { priceId: priceId };
      if (appliedDiscount) {
        requestBody.discountCode = appliedDiscount.code;
      }
      const response = await upgradeSubscriptionPlan(requestBody);
      if (response) {
        if (response?.responseCode === 200) {
          handleUpgradeSubscriptionPopup();
          handlePricingPopup();
          dispatch(showLoader());
          setTimeout(async () => {
            const authDetails = await verifyUserByAuth(token);
            dispatch(updateUserDetail(authDetails?.responseData));
            // Hide the loader
            Utils.successToastMessage(
              "Your subscription plan has been successfully updated"
            );
            dispatch(hideLoader());
          }, 10000); // 5000 milliseconds = 5 seconds
        } else {
          Utils.failureToastMessage(
            "An issue occurred while updating the subscription plan"
          );
          handleUpgradeSubscriptionPopup();
          return;
        }
      }
    } catch (err) {
      Utils.failureToastMessage(
        "An error occurred while updating the subscription plan"
      );
      handleUpgradeSubscriptionPopup();
    } finally {
      // dispatch(hideLoader());
    }
  };
  const handlePlan = () => {
    dispatch(showLoader());
    setTimeout(() => {
      setPlanBasic(!planBasic);
      setPriceId("");
      dispatch(hideLoader());
    }, 500);
  };

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan.title);
    setPriceId(plan.priceId);
    setSelectedProductId(plan.productId || "");
    // Reset discount when switching plans
    setAppliedDiscount(null);
  };

  const handleDiscountApplied = (discount) => {
    setAppliedDiscount(discount);
  };

  // Resolve a priceId -> [planName, planTime, amount] from the live plan lists
  // (replaces the old env-var lookup, which goes stale when prices change).
  const getPlanInfo = (priceId) => {
    if (!priceId) return ["No Plan purchased", "", ""];
    const monthlyMatch = monthlyPlans.find((p) => p.priceId === priceId);
    if (monthlyMatch)
      return [monthlyMatch.title, "month", String(monthlyMatch.amount)];
    const annualMatch = annualPlans.find((p) => p.priceId === priceId);
    if (annualMatch)
      return [annualMatch.title, "year", String(annualMatch.amount)];
    return ["No Plan purchased", "", ""];
  };

  const findMatchingPlan = (matchPriceIdResult, monthlyList, annualList) => {
    const [title, planTime] = matchPriceIdResult;

    if (!title || !planTime) {
      return null;
    }

    const list = planTime === "month" ? monthlyList : annualList;

    return list.find((plan) => plan.title === title) || null;
  };

  const checkPlanStatus = (plan, currentPlan) => {
    return (
      plan.title === currentPlan[0] &&
      ((currentPlan[1] === "month" && plan.amount == currentPlan[2]) ||
        (currentPlan[1] === "year" && plan.amount == currentPlan[2]))
    );
  };

  // Load live plan content (names, prices, bullets) managed in the admin
  // dashboard. Keeps the bundled constants if the request fails so the pricing
  // page never renders empty.
  useEffect(() => {
    let cancelled = false;
    const loadPlans = async () => {
      try {
        const res = await getPlans();
        const apiPlans = res?.responseData || [];
        if (cancelled || !apiPlans.length) return;
        const toItem = (p, period) => ({
          title: p.name,
          amount:
            (period === "month" ? p.monthly?.amount : p.yearly?.amount) ?? 0,
          priceId:
            (period === "month" ? p.monthly?.priceId : p.yearly?.priceId) || "",
          productId: p.productId || "",
          data: p.features || [],
          planTime: period === "month" ? "month" : "year",
          popular: !!p.popular,
          description: p.description || ""
        });
        setMonthlyPlans(apiPlans.map((p) => toItem(p, "month")));
        setAnnualPlans(apiPlans.map((p) => toItem(p, "year")));
      } catch (err) {
        // Keep the bundled fallback plans.
      }
    };
    loadPlans();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const matchedPlan = findMatchingPlan(
      getPlanInfo(isPlanPurchased?.priceId),
      monthlyPlans,
      annualPlans
    );
    if (matchedPlan?.planTime === "year") {
      setPlanBasic(true);
    } else {
      setPlanBasic(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthlyPlans, annualPlans]);

  useEffect(() => {
    setPlanData(planBasic ? annualPlans : monthlyPlans);
  }, [planBasic, monthlyPlans, annualPlans]);

  const handleUpgrade = async () => {
    if (!isPlanPurchased) {
      // First purchase -> backend Stripe Checkout (subscription mode). This funnels
      // discount codes AND free trials through one controlled flow, instead of the
      // old fixed Payment-Link redirect that could not apply either.
      if (!priceId) {
        Utils.failureToastMessage("Please select a plan first");
        return;
      }
      try {
        dispatch(showLoader());
        const body = { priceId, productId: selectedProductId };
        if (appliedDiscount) {
          body.discountCode = appliedDiscount.code;
        }
        const response = await createSubscriptionCheckout(body);
        const checkoutUrl = response?.responseData?.url;
        if (response?.responseCode === 200 && checkoutUrl) {
          window.location.href = checkoutUrl;
        } else {
          Utils.failureToastMessage(
            response?.message ||
              response?.responseMessage ||
              "Unable to start checkout. Please try again."
          );
          dispatch(hideLoader());
        }
      } catch (err) {
        // Backend (HTTPHandler.response) puts the real reason in `message`
        // (e.g. Stripe's "No such price: ..."); surface it instead of a generic toast.
        Utils.failureToastMessage(
          err?.response?.data?.message ||
            err?.response?.data?.responseMessage ||
            "Unable to start checkout. Please try again."
        );
        dispatch(hideLoader());
      }
    } else {
      handleUpgradeSubscriptionPopup();
    }
  };

  const currentPlan = getPlanInfo(isPlanPurchased?.priceId);

  return (
    <div className="fixed right-0 top-0 z-50 flex size-full items-center justify-center bg-blurDark px-15 backdrop-blur-sm">
      {upgradeSubscriptionOpen && (
        <UpgradeSubscription
          handleUpgradeSubscriptionPopup={handleUpgradeSubscriptionPopup}
          updateSubscriptionDetails={updateSubscriptionDetails}
          appliedDiscount={appliedDiscount}
        />
      )}
      <div className="h-90per cursor-pointer overflow-auto rounded-15px bg-white p-7 scrollbar-hide">
        <img
          onClick={handlePricingPopup}
          className="ml-auto cursor-pointer"
          src="/images/close-icon.svg"
          alt="Close"
        />
        <div className="px-22.5">
          <h1 className="text-center font-InterBold text-ft32-41.6 text-black-9">
            Pricing
          </h1>
          <div className="flex justify-center">
            <p className="mt-2.5 w-50per text-center font-InterRegular text-ft18-25.2 text-grey-14">
              {`Choose from one of our plans below to ${
                isPlanPurchased ? "upgrade your current" : "buy"
              } subscription plan.`}
            </p>
          </div>
          <div className="mt-4.5 flex items-center justify-center gap-3.2">
            <p
              className={`${
                !planBasic ? "text-green-12" : "text-grey-14"
              } font-InterRegular text-ft16-19.36`}
            >
              Monthly
            </p>
            <img
              className="cursor-pointer"
              onClick={handlePlan}
              src={
                planBasic
                  ? "/images/enable-switch.svg"
                  : "/images/disable-switch.svg"
              }
              alt="Toggle Plan"
            />
            <p
              className={`${
                planBasic ? "text-green-12" : "text-grey-14"
              } font-InterRegular text-ft16-19.36`}
            >
              Annual
            </p>
          </div>

          {/* Discount Code Input */}
          <div className="mt-6 flex justify-center">
            <div className="w-full max-w-md">
              <DiscountCodeInput
                onDiscountApplied={handleDiscountApplied}
                planId={selectedProductId}
                disabled={!selectedPlan}
              />
            </div>
          </div>

          <div className="mt-8 flex h-full items-center justify-center">
            {planData?.map((item, idx) => (
              <div
                key={idx}
                onClick={() =>
                  isPlanPurchased
                    ? checkPlanStatus(item, currentPlan)
                      ? ""
                      : handleSelectPlan(item)
                    : handleSelectPlan(item)
                }
                className={`relative z-100 ${selectedPlan === item.title ? "h-571px" : " h-130 "} w-75 rounded-15px border ${
                  selectedPlan === item.title
                    ? "border-green-12"
                    : "border-grey-EEEEEE"
                } cursor-pointer bg-white`}
              >
                {item.popular && (
                  <div className="flex h-27px items-center justify-center rounded-t-15px bg-green-12 font-InterRegular text-ft12-16 text-white shadow-priceCard">
                    Most popular
                  </div>
                )}
                <div className="absolute left-20.5 top-12.2 text-center">
                  <h1 className="font-InterBold text-ft24-32 text-green-12">
                    {item.title}
                  </h1>
                  <p className="font-InterRegular text-ft14-19.6 text-grey-12">
                    Start now for
                  </p>
                  <div className="flex items-center justify-center">
                    <p className="font-InterBold text-ft24-32 text-black-14171B">
                      ${item.amount}
                    </p>
                    <p className="pl-1 font-InterRegular text-ft12-16 text-black-12">
                      / {planBasic ? "year" : "month"}
                    </p>
                  </div>
                </div>
                <div className="absolute left-7 top-30">
                  <div className="my-5 ml-1.7 w-50 border border-grey-EEEEEE"></div>
                  <p className="mb-3 font-InterSemibold text-ft18-25.2 text-grey-14">
                    Includes
                  </p>
                  {item.description && (
                    <p className="mb-2 pr-3 font-InterRegular text-ft14-19.6 text-grey-14">
                      {item.description}
                    </p>
                  )}
                  {item.data?.map((data, index) => (
                    <div
                      key={index}
                      className="mt-3 flex w-90per items-center justify-start gap-9px px-3"
                    >
                      <img alt="green tick" src="/images/green-tick.svg" />
                      <span className="font-InterRegular text-ft16-19.36 text-grey-14">
                        {data}
                      </span>
                    </div>
                  ))}
                </div>
                <button
                  disabled={selectedPlan === item.title ? false : true}
                  onClick={handleUpgrade}
                  className={`absolute bottom-5.5 ${selectedPlan === item.title ? "opacity-100" : "opacity-50"} left-19 flex h-8.7 w-30.7 items-center justify-center rounded-2xl font-InterBold text-ft10-16 ${
                    isPlanPurchased
                      ? checkPlanStatus(item, currentPlan)
                        ? "border border-grey-14 text-grey-14"
                        : "bg-green-12 text-white"
                      : "bg-green-12 text-white"
                  }`}
                >
                  {isPlanPurchased
                    ? checkPlanStatus(item, currentPlan)
                      ? "CURRENT PLAN"
                      : "UPGRADE"
                    : "BUY PLAN"}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPopup;
